import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const location = useLocation();
  const isHome = location.pathname === "/";
  const [scrolled, setScrolled] = useState(!isHome);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setScrolled(!isHome);
  }, [isHome]);

  useEffect(() => {
    if (!isHome) return;
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isHome]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const handleMouseEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimer.current = setTimeout(() => setDropdownOpen(false), 200);
  };

  const navTextClass = scrolled
    ? "text-foreground-800 hover:text-foreground-950"
    : "text-white/90 hover:text-white";

  const navBgClass = scrolled
    ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
    : "bg-transparent";

  return (
    <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${navBgClass}`}>
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 group flex-shrink-0"
          >
            <span className={`font-heading text-xl md:text-2xl font-bold tracking-tight transition-colors duration-300 ${
              scrolled ? "text-foreground-900" : "text-white"
            }`}>
              allchinabuy<span className="text-primary-500">.art</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            {/* Home */}
            <Link
              to="/"
              className={`text-sm font-semibold transition-colors whitespace-nowrap ${navTextClass}`}
            >
              Home
            </Link>

            {/* Shopping Guide */}
            <Link
              to="/best-allchinabuy-spreadsheet"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${navTextClass}`}
            >
              Shopping Guide
            </Link>

            {/* Categories Dropdown */}
            <div
              ref={dropdownRef}
              className="relative"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className={`flex items-center gap-1.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${navTextClass}`}
                onClick={() => setDropdownOpen(!dropdownOpen)}
                aria-expanded={dropdownOpen}
              >
                Categories
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-60 bg-background-50 rounded-xl border border-background-200/70 py-3 shadow-lg max-h-[70vh] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap"
                    >
                      <span className="w-7 h-7 flex items-center justify-center rounded-md bg-background-200 flex-shrink-0">
                        <i className={`${cat.icon} text-xs text-foreground-500`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className={`text-sm font-semibold transition-colors whitespace-nowrap ${
                scrolled ? "text-primary-600 hover:text-primary-700" : "text-primary-300 hover:text-primary-200"
              }`}
            >
              AllChinaBuy Spreadsheet
            </a>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="bg-primary-500 text-white text-sm font-semibold px-5 py-2.5 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer flex-shrink-0"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${
              scrolled ? "text-foreground-900" : "text-white"
            }`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden fixed top-16 left-0 right-0 bottom-0 bg-background-50 overflow-y-auto z-40">
          <div className="px-6 py-6 flex flex-col gap-4">
            {/* Primary links */}
            <div className="flex flex-col gap-1">
              <Link
                to="/"
                className="flex items-center gap-3 text-base font-semibold text-foreground-900 py-3 px-3 rounded-lg hover:bg-background-100 transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                <span className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-100 flex-shrink-0">
                  <i className="ri-home-4-line text-primary-600"></i>
                </span>
                Home
              </Link>
              <Link
                to="/best-allchinabuy-spreadsheet"
                className="flex items-center gap-3 text-base font-semibold text-foreground-900 py-3 px-3 rounded-lg hover:bg-background-100 transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                <span className="w-8 h-8 flex items-center justify-center rounded-md bg-accent-100 flex-shrink-0">
                  <i className="ri-book-open-line text-accent-600"></i>
                </span>
                Shopping Guide
              </Link>
            </div>

            {/* Divider */}
            <div className="border-t border-background-200/70 pt-4">
              <p className="text-xs font-semibold text-foreground-400 uppercase tracking-widest mb-3 px-3">
                Categories
              </p>
              <div className="grid grid-cols-2 gap-1">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2.5 text-sm text-foreground-700 py-2.5 px-3 rounded-lg hover:bg-background-100 transition-colors"
                    onClick={() => setMobileOpen(false)}
                  >
                    <span className="w-6 h-6 flex items-center justify-center rounded bg-background-200 flex-shrink-0">
                      <i className={`${cat.icon} text-[10px] text-foreground-500`}></i>
                    </span>
                    <span className="truncate">{cat.name}</span>
                  </a>
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-background-200/70 pt-4 flex flex-col gap-3">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 text-sm font-semibold text-primary-600 py-3 px-3 rounded-lg hover:bg-primary-50 transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                <span className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-100 flex-shrink-0">
                  <i className="ri-external-link-line text-primary-600"></i>
                </span>
                AllChinaBuy Spreadsheet
              </a>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="bg-primary-500 text-white text-sm font-semibold text-center px-5 py-3 rounded-md hover:bg-primary-600 transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}